<template>
  <div class="login-page">
    <div class="login-bg" aria-hidden="true" />
    <div class="login-panel">
      <div class="login-brand">
        <h1 class="login-title">蔡蔡纸样</h1>
        <p class="login-subtitle">后台管理系统</p>
      </div>

      <el-form
        ref="formRef"
        class="login-form"
        :model="form"
        :rules="rules"
        label-position="top"
        size="large"
        @submit.prevent="handleSubmit"
      >
        <el-form-item label="手机号" prop="mobile">
          <el-input
            v-model="form.mobile"
            placeholder="请输入手机号"
            clearable
            maxlength="11"
            autocomplete="tel"
            @keyup.enter="handleSubmit"
          />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            placeholder="请输入密码"
            show-password
            clearable
            autocomplete="current-password"
            @keyup.enter="handleSubmit"
          />
        </el-form-item>
        <el-form-item v-if="!skipCaptcha" label="验证码" prop="captchaCode">
          <div class="captcha-row">
            <el-input
              v-model="form.captchaCode"
              placeholder="请输入验证码"
              clearable
              maxlength="6"
              autocomplete="off"
              @keyup.enter="handleSubmit"
            />
            <button
              type="button"
              class="captcha-img-btn"
              title="点击刷新验证码"
              :disabled="captchaLoading"
              @click="loadCaptcha"
            >
              <img
                v-if="captchaImage"
                :src="captchaImage"
                alt="验证码"
                class="captcha-img"
                @error="onCaptchaImgError"
              />
              <span v-else class="captcha-placeholder">{{ captchaLoadFailed ? '点击重试' : '加载中...' }}</span>
            </button>
          </div>
        </el-form-item>
        <el-form-item class="login-submit-wrap">
          <el-button
            type="primary"
            class="login-submit"
            :loading="loading"
            native-type="submit"
          >
            登 录
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { fetchCaptcha, login } from '../api/auth'
import { resolveCaptchaImage } from '../api/config'
import { extractApiError } from '../api/error'
import { clearAuth, saveAuth, sanitizeRedirect, isAuthenticated } from '../utils/auth'
import { getApiBaseUrl } from '../utils/request'

const router = useRouter()
const route = useRoute()
const formRef = ref(null)
const loading = ref(false)
/** 忽略过期登录请求的响应（防止连点导致成功后又弹验证码错误） */
let loginSeq = 0
const captchaLoading = ref(false)
const captchaLoadFailed = ref(false)
const captchaImage = ref('')
const captchaId = ref('')

const form = reactive({
  mobile: '',
  password: '',
  captchaCode: ''
})

/** 连本地 Java 时可跳过验证码 */
const skipCaptcha = computed(() => {
  const flag = String(process.env.VUE_APP_SKIP_CAPTCHA || '').toLowerCase()
  if (flag === '1' || flag === 'true' || flag === 'yes') return true
  const proxy = String(process.env.VUE_APP_PROXY_TARGET || '')
  if (/127\.0\.0\.1|localhost/i.test(proxy)) return true
  const base = getApiBaseUrl() || String(process.env.VUE_APP_API_BASE || '')
  return /^(https?:\/\/)?(127\.0\.0\.1|localhost)(:\d+)?/i.test(base)
})

const MOBILE_PATTERN = /^1[3-9]\d{9}$/

const rules = computed(() => {
  const base = {
    mobile: [
      { required: true, message: '请输入手机号', trigger: 'blur' },
      { pattern: MOBILE_PATTERN, message: '手机号格式不正确', trigger: 'blur' }
    ],
    password: [
      { required: true, message: '请输入密码', trigger: 'blur' },
      { min: 8, message: '密码至少 8 位', trigger: 'blur' }
    ]
  }
  if (!skipCaptcha.value) {
    base.captchaCode = [
      { required: true, message: '请输入验证码', trigger: 'blur' },
      { pattern: /^[A-Za-z0-9]{4,6}$/, message: '验证码为 4-6 位字母或数字', trigger: 'blur' }
    ]
  }
  return base
})

onMounted(() => {
  clearAuth()
  if (!skipCaptcha.value) {
    loadCaptcha()
  }
})

async function loadCaptcha() {
  if (skipCaptcha.value) return
  captchaLoading.value = true
  captchaLoadFailed.value = false
  form.captchaCode = ''
  captchaImage.value = ''
  captchaId.value = ''
  try {
    const { data } = await fetchCaptcha()
    if (!data?.captchaId) {
      throw new Error('验证码响应无效')
    }
    captchaId.value = data.captchaId
    captchaImage.value = resolveCaptchaImage(data)
  } catch (error) {
    captchaLoadFailed.value = true
    ElMessage.error(extractApiError(error, '验证码加载失败，请检查后端服务或 API 地址'))
  } finally {
    captchaLoading.value = false
  }
}

async function onCaptchaImgError() {
  captchaLoadFailed.value = true
  captchaImage.value = ''
  ElMessage.warning('验证码图片加载失败，请点击刷新')
}

async function handleSubmit() {
  if (loading.value) return
  const formEl = formRef.value
  if (!formEl) return
  const valid = await formEl.validate().catch(() => false)
  if (!valid) return
  if (!skipCaptcha.value && !captchaId.value) {
    ElMessage.warning('验证码未就绪，请稍后重试')
    await loadCaptcha()
    return
  }

  const seq = ++loginSeq
  loading.value = true
  try {
    const { data } = await login(
      form.mobile.trim(),
      form.password,
      skipCaptcha.value ? '' : captchaId.value,
      skipCaptcha.value ? '' : form.captchaCode.trim()
    )
    if (seq !== loginSeq) return
    if (!data?.accessToken) {
      ElMessage.error('登录响应异常，请稍后重试')
      return
    }
    saveAuth(data.accessToken, data.expiresInSeconds)
    ElMessage.success('登录成功')
    const redirect = sanitizeRedirect(route.query.redirect)
    if (redirect) {
      router.push(redirect)
    } else {
      router.push({ name: 'home' })
    }
  } catch (error) {
    // 已成功登录或发起了更新的请求时，不再提示过期验证码错误
    if (seq !== loginSeq || isAuthenticated()) return
    ElMessage.error(extractApiError(error, '登录失败'))
    if (!skipCaptcha.value) {
      await loadCaptcha()
    }
  } finally {
    if (seq === loginSeq) {
      loading.value = false
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
}

.login-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    #e8eef5 0%,
    #dce8f2 35%,
    #cfd9e8 70%,
    #c5d4e3 100%
  );
  z-index: 0;
}

.login-bg::after {
  content: '';
  position: absolute;
  inset: -40%;
  background:
    radial-gradient(ellipse at 20% 30%, rgba(99, 140, 180, 0.22) 0%, transparent 55%),
    radial-gradient(ellipse at 80% 70%, rgba(120, 90, 140, 0.12) 0%, transparent 50%);
  pointer-events: none;
}

.login-panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  padding: 40px 36px 36px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 16px;
  box-shadow:
    0 4px 24px rgba(45, 62, 80, 0.08),
    0 1px 3px rgba(45, 62, 80, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
}

.login-brand {
  text-align: center;
  margin-bottom: 28px;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #2c3e50;
}

.login-subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  color: #8492a6;
}

.login-form :deep(.el-form-item__label) {
  color: #5a6a7a;
  font-weight: 500;
}

.captcha-row {
  display: flex;
  gap: 12px;
  width: 100%;
  align-items: stretch;
}

.captcha-row :deep(.el-input) {
  flex: 1;
  min-width: 0;
}

.captcha-img-btn {
  flex-shrink: 0;
  width: 132px;
  height: 40px;
  padding: 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.captcha-img-btn:disabled {
  cursor: wait;
  opacity: 0.7;
}

.captcha-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.captcha-placeholder {
  font-size: 12px;
  color: #909399;
}

.login-submit-wrap {
  margin-bottom: 0;
  margin-top: 8px;
}

.login-submit {
  width: 100%;
}
</style>
